import { Injectable } from '@angular/core';
import * as THREE from 'three';
import { CetvelGeometryService } from './cetvel-geometry.service';

@Injectable({
  providedIn: 'root'
})
export class CetvelSelectionService {

  // 🎯 Çizgi tipleri - FikoSecililer.ts ile senkron
  private readonly LINE_TYPES = ['LineSegments', 'LineSegments2', 'Line2', 'LineGeometry', 'Line'];

  constructor(private geomService: CetvelGeometryService) { }

  /**
   * Kesişimin çizgi/kenar olup olmadığını kontrol et
   */
  private isLineIntersection(kesisim: any): boolean {
    if (!kesisim) return false;

    const objectType = kesisim.object?.type;
    const altObjeType = kesisim.altObje?.type;

    // Doğrudan çizgi objesi
    if (this.LINE_TYPES.includes(objectType)) {
      return true;
    }

    // altObje'de çizgi
    if (altObjeType && this.LINE_TYPES.includes(altObjeType)) {
      return true;
    }

    // FikoCizimYalin içinde çizgi
    if (objectType === 'FikoCizimYalin' && kesisim.altObje && this.LINE_TYPES.includes(altObjeType)) {
      return true;
    }

    return false;
  }

  /**
   * Kesişimin nokta olup olmadığını kontrol et
   */
  private isPointIntersection(kesisim: any): boolean {
    if (!kesisim) return false;

    const objectType = kesisim.object?.type;
    const altObjeType = kesisim.altObje?.type;

    return objectType === 'Points' || altObjeType === 'Points';
  }

  /**
   * Seçim 1 tipi belirle (Yüzey, Çizgi, Nokta, vs)
   */
  determineSonuc1Type(
    kesisim: any,
    secim1Filitre: string,
    fikoSecim1: any,
    onYuzeyTipTespit: (geom: THREE.BufferGeometry | undefined, index: 1 | 2) => void,
    onCizgiGeometriAl: (selection: any, lineSegments: THREE.LineSegments, kesisim?: any) => THREE.BufferGeometry
  ): {
    tipi: "Nokta" | "Çizgi" | "Yüzey" | "Grup" | null;
    secilen: any;
    secilenGeom: THREE.BufferGeometry | null;
    secilenPointIndex: number | null;
  } {
    // NOKTA KOY MODU: Sadece noktayı yerleştir, diğer nesneleri yoksay
    if (secim1Filitre.indexOf("Nokta Koy") > -1) {
      return {
        tipi: 'Nokta',
        secilen: kesisim,
        secilenGeom: null,
        secilenPointIndex: (kesisim as any)?.closestPointIndex ?? null
      };
    }

    // 🎯 ÖNEMLİ: Kenar ve nokta öncelikli kontrol sırası:
    // 1. Çizgi (ÖNCELİKLİ) - face olsa bile çizgi öncelikli
    // 2. Nokta (ÖNCELİKLİ)
    // 3. Yüzey (face varsa)

    const isLine = this.isLineIntersection(kesisim);
    const isPoint = this.isPointIntersection(kesisim);

    /** 1. ÖNCELİK: Seçilen Çizgi İse */
    if (isLine) {
      let secilen: THREE.LineSegments = (kesisim?.altObje || kesisim?.object) as THREE.LineSegments;
      if (!secilen) {
        return {
          tipi: null,
          secilen: null,
          secilenGeom: null,
          secilenPointIndex: null
        };
      } else {
        return {
          tipi: 'Çizgi',
          secilen: kesisim,
          secilenGeom: onCizgiGeometriAl(fikoSecim1, secilen, kesisim),
          secilenPointIndex: null
        };
      }
    }

    /** 2. ÖNCELİK: Seçilen Nokta İse */
    if (isPoint) {
      let secilen: THREE.Points = (kesisim?.altObje || kesisim?.object) as THREE.Points;

      if (kesisim?.object?.type === "FikoCizimYalin" && kesisim?.altObje) {
        secilen = kesisim.altObje as THREE.Points;
      }

      if (!secilen) {
        return {
          tipi: null,
          secilen: null,
          secilenGeom: null,
          secilenPointIndex: null
        };
      } else {
        return {
          tipi: 'Nokta',
          secilen: kesisim,
          secilenGeom: null,
          secilenPointIndex: (kesisim as any)?.closestPointIndex ?? null
        };
      }
    }

    /** 3. SONRA: face varsa yüzeydir */
    if (kesisim?.face) {
      let secilen: THREE.Mesh = (kesisim?.altObje || kesisim?.object) as THREE.Mesh;
      if (!secilen) {
        onYuzeyTipTespit(undefined, 1);
        return {
          tipi: null,
          secilen: null,
          secilenGeom: null,
          secilenPointIndex: null
        };
      } else {
        let secilenGeom: THREE.BufferGeometry | null = null;
        if (fikoSecim1.sonSecilenHighlightGeometriYuzey) {
          secilenGeom = fikoSecim1.sonSecilenHighlightGeometriYuzey.clone();
        }
        // 🎯 ANLIK YÜZEY GEOMETRİSİ: highlight cache'ine bağımlı kalma — tıklanan face
        // grubunu doğrudan üret (dev-düzlem fallback'i yüzey-yüzey ölçümünü 0 yapıyordu)
        if (!secilenGeom && typeof fikoSecim1.seciliYuzeyGeometrisiAl === 'function') {
          secilenGeom = fikoSecim1.seciliYuzeyGeometrisiAl(kesisim);
        }
        if (!secilenGeom && kesisim.normal) {
          const normal1 = (kesisim.normal as THREE.Vector3).clone().normalize();
          const point1 = kesisim.point as THREE.Vector3;
          secilenGeom = this.geomService.normalDanPlaneGeometriOlustur(normal1, point1, 1000);
        }
        onYuzeyTipTespit(secilenGeom, 1);
        return {
          tipi: 'Yüzey',
          secilen: kesisim,
          secilenGeom: secilenGeom,
          secilenPointIndex: null
        };
      }
    }

    // Tanınmayan tip
    return {
      tipi: null,
      secilen: null,
      secilenGeom: null,
      secilenPointIndex: null
    };
  }

  /**
   * Seçim 2 tipi belirle (aynı mesh/face kontrolü ile)
   */
  determineSonuc2Type(
    kesisim: any,
    secim2Filitre: string,
    fikoSecim2: any,
    secilen1: any,
    secilen1FaceIndex: number | null,
    onYuzeyTipTespit: (geom: THREE.BufferGeometry | undefined, index: 1 | 2) => void,
    onCizgiGeometriAl: (selection: any, lineSegments: THREE.LineSegments, kesisim?: any) => THREE.BufferGeometry
  ): {
    tipi: "Nokta" | "Çizgi" | "Yüzey" | "Grup" | null;
    secilen: any;
    secilenGeom: THREE.BufferGeometry | null;
    secilenPointIndex: number | null;
  } {
    // NOKTA KOY MODU: Sadece noktayı yerleştir, diğer nesneleri yoksay
    if (secim2Filitre.indexOf("Nokta Koy") > -1) {
      return {
        tipi: 'Nokta',
        secilen: kesisim,
        secilenGeom: null,
        secilenPointIndex: (kesisim as any)?.closestPointIndex ?? null
      };
    }

    // Seçim 1'in mesh'i ile aynı mı kontrol et (aynı mesh ve aynı face ise reddet)
    const secilen1Mesh = secilen1?.object;
    const secilen1FaceIndexValue = secilen1FaceIndex;

    // 🎯 ÖNEMLİ: Kenar ve nokta öncelikli kontrol sırası (Seçim 1 ile aynı)
    const isLine = this.isLineIntersection(kesisim);
    const isPoint = this.isPointIntersection(kesisim);

    /** 1. ÖNCELİK: Seçilen Çizgi İse */
    if (isLine) {
      let secilen: THREE.LineSegments = (kesisim?.altObje || kesisim?.object) as THREE.LineSegments;
      if (!secilen) {
        return {
          tipi: null,
          secilen: null,
          secilenGeom: null,
          secilenPointIndex: null
        };
      } else {
        return {
          tipi: 'Çizgi',
          secilen: kesisim,
          secilenGeom: onCizgiGeometriAl(fikoSecim2, secilen, kesisim),
          secilenPointIndex: null
        };
      }
    }

    /** 2. ÖNCELİK: Seçilen Nokta İse */
    if (isPoint) {
      let secilen: THREE.Points = (kesisim?.altObje || kesisim?.object) as THREE.Points;

      if (kesisim?.object?.type === "FikoCizimYalin" && kesisim?.altObje) {
        secilen = kesisim.altObje as THREE.Points;
      }

      if (!secilen) {
        return {
          tipi: null,
          secilen: null,
          secilenGeom: null,
          secilenPointIndex: null
        };
      } else {
        return {
          tipi: 'Nokta',
          secilen: kesisim,
          secilenGeom: null,
          secilenPointIndex: (kesisim as any)?.closestPointIndex ?? null
        };
      }
    }

    /** 3. SONRA: face varsa yüzeydir */
    if (kesisim?.face) {
      let secilen: THREE.Mesh = (kesisim?.altObje || kesisim?.object) as THREE.Mesh;
      if (!secilen) {
        onYuzeyTipTespit(undefined, 2);
        return {
          tipi: null,
          secilen: null,
          secilenGeom: null,
          secilenPointIndex: null
        };
      }

      // Aynı mesh'in aynı face'i ise reddet
      if (secilen1Mesh === secilen && secilen1FaceIndexValue === (kesisim as any).faceIndex) {
        return {
          tipi: null,
          secilen: null,
          secilenGeom: null,
          secilenPointIndex: null
        };
      }

      let secilenGeom: THREE.BufferGeometry | null = null;
      if (fikoSecim2.sonSecilenHighlightGeometriYuzey) {
        secilenGeom = fikoSecim2.sonSecilenHighlightGeometriYuzey.clone();
      }
      // 🎯 ANLIK YÜZEY GEOMETRİSİ (bkz. seçim 1 açıklaması)
      if (!secilenGeom && typeof fikoSecim2.seciliYuzeyGeometrisiAl === 'function') {
        secilenGeom = fikoSecim2.seciliYuzeyGeometrisiAl(kesisim);
      }
      if (!secilenGeom && kesisim.normal) {
        const normal2 = (kesisim.normal as THREE.Vector3).clone().normalize();
        const point2 = kesisim.point as THREE.Vector3;
        secilenGeom = this.geomService.normalDanPlaneGeometriOlustur(normal2, point2, 1000);
      }
      onYuzeyTipTespit(secilenGeom, 2);
      return {
        tipi: 'Yüzey',
        secilen: kesisim,
        secilenGeom: secilenGeom,
        secilenPointIndex: null
      };
    }

    // Tanınmayan tip
    return {
      tipi: null,
      secilen: null,
      secilenGeom: null,
      secilenPointIndex: null
    };
  }
}
